/**
 * Barra fina no topo da página enquanto há dados chegando (consultas do React Query)
 * ou uma tela sendo baixada (rotas sob demanda). Só aparece se a espera passar de 200 ms,
 * para não piscar em respostas rápidas.
 *
 * Movimento (23/10/2026, guia/09 "Barra fina no topo"): é a .ds-barra-topo. Em vez da barra que
 * avançava devagar até 85% e completava no fim (fingia saber quanto falta), um trecho corre de
 * um lado ao outro enquanto espera (laço de 1,4 s, com transform) e, quando a página chega, a
 * barra some com fade de --duracao-140. Com "reduzir movimento", uma linha parada.
 */
import { useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useNavigation } from 'react-router-dom';
import { tokenMs } from '../../lib/movimento';

const SHOW_AFTER_MS = 200;

export function TopProgress({ extraBusy = false }: { extraBusy?: boolean }): JSX.Element | null {
  const fetching = useIsFetching();
  const navigation = useNavigation();
  const busy = fetching > 0 || navigation.state !== 'idle' || extraBusy;
  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const timer = useRef(0);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (busy) {
      timer.current = window.setTimeout(() => setPhase('running'), SHOW_AFTER_MS);
    } else {
      setPhase((p) => (p === 'running' ? 'done' : 'idle'));
      // Sai da página depois do fade (o degrau de saída).
      timer.current = window.setTimeout(() => setPhase('idle'), tokenMs('--duracao-140'));
    }
    return () => window.clearTimeout(timer.current);
  }, [busy]);

  if (phase === 'idle') return null;
  return (
    <div
      className={`top-progress ds-barra-topo${phase === 'done' ? ' is-saindo' : ''}`}
      aria-hidden="true"
    />
  );
}
